import { GQLPosts, GQLTags, GQLCategories, GQLUrls } from "@gql/types/gql";
import RelatedTags from "@components/shared/relatedTags";
import {
    addNoFollowToLinks,
    addStyleClassToIframe,
    addStyleClassToTable,
    getPublishedDateLabel,
    resolveURL,
    updateAnchorHref,
} from "@lib/sharedUtils";
import Image from "next/legacy/image";
import blogImgPlaceholder from "@public/placeHolder844x538.png";
import { PLACE_HOLDER_IMAGE_PATH, titleDecoder } from "@lib/sharedUtils";
import Form from "@components/shared/form";
import { useQuery } from "@tanstack/react-query";
import { Session } from "src/api/session";
import Share from "@components/shared/share/Share";
import ListOfContentsMobile from "@components/shared/listOfContentsMobile";
import RecentPostsMobile from "@components/shared/recentPostsMobile";
import AuthorInfo from "./authorInfo";
import { useMemo } from "react";
export default function MainContent({
    blog,
    category,
    listOfContentsMobile,
    recentPostsMobile,
    isNews = false,
}: {
    blog: GQLPosts;
    category?: GQLCategories | undefined;
    listOfContentsMobile: { title: string | null; link: string }[];
    recentPostsMobile: { title: string | null; link: string }[];
    isNews?: boolean;
}) {
    const session = useQuery<Session>({ queryKey: ["session"] });

    const publishedDateLabel = useMemo(() => {
        return getPublishedDateLabel(blog);
    }, [blog]);

    if (blog) {
        return (
            <section
                className="lg:w-2/3 lg:px-0 mt-9 lg:-mt-3"
                itemScope
                itemType={`https://schema.org/${
                    isNews ? "NewsArticle" : "blog"
                }`}
            >
                <h1
                    className={`lg:text-3xl text-2xl lg:leading-[45px] leading-8 font-bold`}
                    itemProp="name"
                >
                    {titleDecoder(blog.title as string, {
                        category: category
                            ? ((category.label ?? category.name) as string)
                            : "",
                        region:
                            (session?.data?.visitDetails?.region as string) ??
                            "",
                    })}
                </h1>
                {blog.writerUser?.nickname && <AuthorInfo mainInfo={blog} />}
                <Share
                    contentToBeSharedUrl={blog?.url?.url as string}
                    secondStyle
                />
                {blog.image?.fullPath ? (
                    <>
                        <div
                            className={`mt-6 relative w-full`}
                            itemProp="image"
                        >
                            <img
                                src={blogImgPlaceholder.src}
                                alt={blog.image.alt || (blog.title as string)}
                            />
                            <Image
                                src={blog.image?.fullPath}
                                alt={blog.image.alt || (blog.title as string)}
                                width={844}
                                height={538}
                                layout="fill"
                                objectFit="cover"
                                className="block w-full"
                                sizes="(max-width: 768px) 100vw,
                                    (max-width: 1200px) 50vw,
                                    60vw"
                            />
                        </div>
                        {blog.image?.caption && (
                            <div className="text-gray-500 text-sm bg-[#fffcf1] px-2 py-1">
                                {blog.image?.caption}
                            </div>
                        )}
                    </>
                ) : (
                    <div className="mt-6">
                        <img
                            src={PLACE_HOLDER_IMAGE_PATH}
                            alt="logo"
                            width={844}
                            height={538}
                        />
                    </div>
                )}
                <div className="py-1">
                    <em>
                        <small>{publishedDateLabel}</small>
                    </em>
                </div>
                <div className="postContainer" itemProp="text">
                    {blog.excerpt && (
                        <div
                            className=" mt-3 "
                            dangerouslySetInnerHTML={{
                                __html: addNoFollowToLinks(
                                    titleDecoder(blog.excerpt, {
                                        category: category?.name as string,
                                        region:
                                            session.data?.visitDetails
                                                ?.region ?? "",
                                    }),
                                ),
                            }}
                        />
                    )}
                </div>
                {listOfContentsMobile?.length > 0 && (
                    <ListOfContentsMobile contents={listOfContentsMobile} />
                )}
                {!isNews && <Form />}
                <div className="newsContainer" itemProp="text">
                    <div
                        dangerouslySetInnerHTML={{
                            __html: addStyleClassToTable(
                                addStyleClassToIframe(
                                    addNoFollowToLinks(
                                        titleDecoder(
                                            updateAnchorHref(
                                                blog.body as string,
                                                resolveURL(
                                                    blog.url ??
                                                        (category?.url as GQLUrls),
                                                ),
                                            ),

                                            {
                                                category:
                                                    category?.name as string,
                                                region:
                                                    session.data?.visitDetails
                                                        ?.region ?? "",
                                            },
                                        ),
                                    ),
                                ),
                            ),
                        }}
                    />
                </div>
                {isNews && <Form />}
                {blog.writerUser?.nickname && blog.writerUser?.bio && (
                    <AuthorInfo
                        mainInfo={blog}
                        withBio
                        category={category?.name as string}
                        region={session.data?.visitDetails?.region ?? ""}
                    />
                )}
                {blog?.tags?.length ? (
                    <RelatedTags
                        tags={blog.tags as GQLTags[]}
                        isNews={isNews}
                    />
                ) : (
                    <></>
                )}
                {recentPostsMobile?.length > 0 && (
                    <RecentPostsMobile links={recentPostsMobile} />
                )}
            </section>
        );
    }
    return <></>;
}
